import { useMutation, useQuery, useQueryClient } from 'react-query'

import { billingEngineRepository } from '@/src/api/BillingEngineRepository'
import { useCurrentTeamId } from '@/src/hooks/useCurrentTeamId'
import type { BillingProfile } from '@/src/types/billingEngine'

export function billingErrorMessage(error: unknown): string {
  const response = error as { data?: { error?: string } }
  return response?.data?.error || (error instanceof Error ? error.message : 'Please try again or contact support.')
}

export function useBillingProfile() {
  const teamId = useCurrentTeamId()
  return useQuery(['billing-engine-profile', teamId], async () => (await billingEngineRepository.getBillingProfile()).data, { enabled: Boolean(teamId), staleTime: 30_000 })
}

export function useUpdateBillingProfile() {
  const teamId = useCurrentTeamId()
  const client = useQueryClient()
  return useMutation(async (profile: BillingProfile) => (await billingEngineRepository.updateBillingProfile(profile)).data, {
    onSuccess: (data) => {
      client.setQueryData(['billing-engine-profile', teamId], data)
    },
  })
}

export function useCreditPurchases(page: number, perPage: number) {
  const teamId = useCurrentTeamId()
  return useQuery(['billing-engine-purchases', teamId, page, perPage], async () => (await billingEngineRepository.getCreditPurchases(page, perPage)).data, {
    enabled: Boolean(teamId),
    staleTime: 10_000,
    refetchInterval: (data) => (data?.purchases.some((purchase) => purchase.invoice_state === 'pending') ? 5000 : false),
  })
}

export function useCreatePurchaseInvoice() {
  const teamId = useCurrentTeamId()
  const client = useQueryClient()
  return useMutation(async ({ id, profile }: { id: number; profile: BillingProfile }) => (await billingEngineRepository.createPurchaseInvoice(id, profile)).data, {
    onSuccess: () => {
      client.invalidateQueries(['billing-engine-purchases', teamId])
      client.invalidateQueries(['billing-engine-profile', teamId])
    },
  })
}
